// SYSTEM_ROUTE: /reminders
import { useMemo, useState } from 'react';
import type { DeliveryRecord, Reminder } from '@life-os/app-core';
import { DEFAULT_QUIET_HOURS, isReminderPending } from '@life-os/app-core';
import { InAppChannel } from '@life-os/data';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const STATE_TONE: Record<Reminder['state'], ChipTone> = {
  scheduled: 'primary',
  snoozed: 'warn',
  fired: 'neutral',
  done: 'muted',
  dismissed: 'muted',
};

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

function defaultFireAt(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  // datetime-local wants YYYY-MM-DDTHH:mm
  return d.toISOString().slice(0, 16);
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

export function RemindersScreen() {
  const services = useServices();
  const { revision, refresh, data } = useStore();
  const [label, setLabel] = useState('');
  const [when, setWhen] = useState(defaultFireAt());
  const [quietOn, setQuietOn] = useState(true);
  const [deliverMsg, setDeliverMsg] = useState('');
  const channel = useMemo(() => new InAppChannel(), []);

  const reminders = useAsync<readonly Reminder[]>(() => services.reminders.listAll(), [revision]);
  const log = useAsync<readonly DeliveryRecord[]>(() => data.notifications.log(), [revision]);
  const all = reminders.value ?? [];
  const pending = all.filter((r) => isReminderPending(r.state));
  const done = all.filter((r) => !isReminderPending(r.state));

  const schedule = async () => {
    if (label.trim().length === 0) return;
    await services.reminders.schedule({ label, fireAt: new Date(when).toISOString() });
    setLabel('');
    refresh();
  };

  const deliverDue = async () => {
    const quietHours = quietOn ? DEFAULT_QUIET_HOURS : { ...DEFAULT_QUIET_HOURS, enabled: false };
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    const summary = await data.notifications.process(
      pending,
      { nowIso: new Date().toISOString(), quietHours, online },
      channel,
    );
    await Promise.all(summary.deliveredIds.map((id) => services.reminders.setState(id as Reminder['id'], 'fired')));
    const parts = [`${summary.delivered} delivered`];
    if (summary.held > 0) parts.push(`${summary.held} held (quiet hours)`);
    if (summary.offline > 0) parts.push(`${summary.offline} waiting for network`);
    setDeliverMsg(parts.join(' · '));
    refresh();
  };

  const snooze = async (r: Reminder) => {
    await services.reminders.setState(r.id, 'snoozed');
    refresh();
  };

  return (
    <>
      <ScreenHeader title={<>Reminders & Notifications</>} subtitle={<>Local-first nudges with a clear delivery state. Redacted by default and shown on Today — no
            noisy channels until you wire them up.</>} />

      <Pane title="New reminder">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 220px' }}
            value={label}
            placeholder="Remind me to…"
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void schedule();
            }}
            aria-label="Reminder label"
          />
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            aria-label="Fire at"
            style={inputStyle}
          />
          <Button variant="primary" onClick={() => void schedule()}>
            Schedule
          </Button>
        </div>
      </Pane>

      <Pane title="Pending" hint={`${pending.length}`}>
        <Stack>
          {pending.length === 0 ? (
            <EmptyState title="Nothing pending">Schedule a reminder above. It will appear on Today when it's due.</EmptyState>
          ) : (
            pending.map((r) => (
              <CompactRow
                key={String(r.id)}
                title={r.label}
                subtitle={fmt(r.fireAt)}
                meta={
                  <>
                    <Chip tone={STATE_TONE[r.state]}>{r.state}</Chip>
                    {r.state !== 'snoozed' ? (
                      <Button variant="ghost" onClick={() => void snooze(r)}>
                        Snooze
                      </Button>
                    ) : null}
                    <Button variant="primary" onClick={() => void services.reminders.setState(r.id, 'done').then(refresh)}>
                      Done
                    </Button>
                    <Button variant="ghost" onClick={() => void services.reminders.setState(r.id, 'dismissed').then(refresh)}>
                      Dismiss
                    </Button>
                  </>
                }
              />
            ))
          )}
        </Stack>
      </Pane>

      {done.length > 0 ? (
        <Pane title="Past" hint={`${done.length}`}>
          <Stack>
            {done.map((r) => (
              <CompactRow key={String(r.id)} title={r.label} subtitle={fmt(r.fireAt)} meta={<Chip tone={STATE_TONE[r.state]}>{r.state}</Chip>} />
            ))}
          </Stack>
        </Pane>
      ) : null}

      <Pane
        title="Delivery"
        hint="Run the local delivery pass. Quiet hours hold nudges; sensitive content is redacted on limited channels."
      >
        <Stack>
          <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--lc-text-sm)' }}>
              <input type="checkbox" checked={quietOn} onChange={(e) => setQuietOn(e.target.checked)} aria-label="Quiet hours" />
              Quiet hours 22:00–07:00 UTC
            </label>
            <Button variant="primary" onClick={() => void deliverDue()}>Deliver due now</Button>
            {deliverMsg ? <Chip tone="primary">{deliverMsg}</Chip> : null}
          </div>
          {(log.value ?? []).length === 0 ? (
            <EmptyState title="No delivery history yet">Run a delivery pass to see an auditable log of what was sent, held, or redacted.</EmptyState>
          ) : (
            (log.value ?? []).slice(0, 12).map((d) => (
              <CompactRow
                key={d.id}
                title={`${d.outcome.replace(/_/g, ' ')}${d.redacted ? ' · redacted' : ''}`}
                subtitle={`${d.channel} · ${fmt(d.at)}${d.detail ? ` · ${d.detail}` : ''}`}
                meta={<Chip tone={d.outcome === 'delivered' ? 'primary' : d.outcome === 'failed' ? 'danger' : 'warn'}>{d.outcome === 'delivered' ? 'sent' : d.outcome === 'held_quiet_hours' ? 'held' : d.outcome}</Chip>}
              />
            ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
